import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import {
  useUnreadCount,
  useMarkAllAsRead,
} from "@/hooks/domains/notifications/useNotifications";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import NotificationsDropdown from "./NotificationsDropdown";
import { cn } from "@/lib/utils";

export default function NotificationBell() {
  const { user } = useAuth();
  const { data: unreadCount = 0, isLoading } = useUnreadCount(user?.id);
  const markAllAsReadMutation = useMarkAllAsRead();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const markReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Animate bell when new notifications arrive
  useEffect(() => {
    if (unreadCount > 0) {
      setHasNewNotifications(true);
      const timer = setTimeout(() => setHasNewNotifications(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    // Clean up any existing timeout
    if (markReadTimeoutRef.current) {
      clearTimeout(markReadTimeoutRef.current);
      markReadTimeoutRef.current = null;
    }

    // Set new timeout if opening with unread notifications
    if (open && unreadCount > 0 && user?.id) {
      markReadTimeoutRef.current = setTimeout(() => {
        if (user?.id) {
          markAllAsReadMutation.mutate(user.id);
        }
        markReadTimeoutRef.current = null;
      }, 3000); // 3 seconds delay before marking as read
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (markReadTimeoutRef.current) {
        clearTimeout(markReadTimeoutRef.current);
      }
    };
  }, []);

  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "relative p-2 rounded-full transition-all duration-200",
            "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50",
            hasNewNotifications && "animate-pulse"
          )}
          aria-label={`Notifications${
            unreadCount > 0 ? ` (${unreadCount} unread)` : ""
          }`}
          disabled={isLoading}
        >
          <Bell
            className={cn(
              "h-5 w-5 text-gray-600 transition-transform duration-200",
              hasNewNotifications && "scale-110"
            )}
          />
          {unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 9 ? "9+" : unreadCount}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-white border border-gray-200 shadow-lg rounded-lg"
        align="end"
        sideOffset={8}
      >
        <NotificationsDropdown />
      </PopoverContent>
    </Popover>
  );
}
